'use client';

import classNames from 'classnames';
import { ComponentProps, useCallback } from 'react';
import { useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import SwiperCarousel from '@alltrails/denali/components/SwiperCarousel';
import styles from './styles/styles.module.scss';

type PhotoCarouselProps = {
  arrowStyle?: ComponentProps<typeof SwiperCarousel>['arrowStyle'];
  uniqueId: string;
  photos: (string | { url: string; alt: string })[];
  loadPhotos?: () => void;
  lazyLoadImages?: boolean;
  zoomPhoto?: boolean;
  hasPaginationGradient?: boolean;
  aspectRatio?: 'square' | 'wide' | 'trail-standard-card';
  onStartInteracting?: React.ComponentProps<typeof SwiperCarousel>['onStartInteracting'];
  onStopInteracting?: React.ComponentProps<typeof SwiperCarousel>['onStopInteracting'];
  isLoading?: boolean;
};

const aspectRatiosToClassNames = {
  square: styles.square,
  wide: styles.wide,
  'trail-standard-card': styles.trailStandardCard
};

const PhotoCarousel = ({
  arrowStyle = 'subtle',
  uniqueId,
  photos,
  loadPhotos,
  zoomPhoto = false,
  aspectRatio = 'wide',
  hasPaginationGradient = false,
  lazyLoadImages,
  onStartInteracting,
  onStopInteracting,
  isLoading = false
}: PhotoCarouselProps) => {
  const intl = useIntl();
  const aspectRatioClass = aspectRatiosToClassNames[aspectRatio];
  const imgLoading = lazyLoadImages ? 'lazy' : 'eager';

  /**
   * Generates accessible alt text for carousel photos with internationalization support.
   *
   * @param photo - Either a URL string or an object containing url and alt text
   * @param index - Zero-based index of the current photo in the carousel
   * @returns Localized alt text for the photo
   *
   * @example
   * // For string photos without uniqueId:
   * getAltText('photo.jpg', 0) // Returns: "Photo 1 of 3"
   *
   * @example
   * // For string photos with uniqueId (trail name):
   * getAltText('photo.jpg', 1) // Returns: "Photo 2 of 3 of Great Falls Park"
   *
   * @example
   * // For photo objects with custom alt text:
   * getAltText({ url: 'photo.jpg', alt: 'Sunset view' }, 0) // Returns: "Sunset view"
   */
  const getAltText = useCallback(
    (photo: string | { url: string; alt: string }, index: number) => {
      if (typeof photo === 'string') {
        if (uniqueId) {
          return intl.formatMessage(
            { defaultMessage: 'Photo {currentPhoto} of {totalPhotos} of {name}' },
            { currentPhoto: index + 1, totalPhotos: photos.length, name: uniqueId }
          );
        }
        return intl.formatMessage(
          { defaultMessage: 'Photo {currentPhoto} of {totalPhotos}' },
          { currentPhoto: index + 1, totalPhotos: photos.length }
        );
      }
      return photo.alt;
    },
    [uniqueId, intl, photos.length]
  );

  if (isLoading) {
    return (
      <ShimmerContainer className={classNames(styles.container, aspectRatioClass)}>
        <div className={styles.shimmerContent} />
      </ShimmerContainer>
    );
  }

  if (photos.length === 0) {
    return <div className={classNames(styles.container, aspectRatioClass)} />;
  }

  if (photos.length === 1) {
    const photo = photos[0];
    const src = typeof photo === 'string' ? photo : photo.url;
    const alt = getAltText(photo, 0);

    return (
      <div className={classNames(styles.container, aspectRatioClass, zoomPhoto && styles.zoomPhoto)}>
        <Image key={src} src={src} alt={alt} className={styles.image} loading={imgLoading} unoptimized width={0} height={0} />
      </div>
    );
  }

  return (
    <SwiperCarousel
      className={classNames(styles.container, aspectRatioClass, zoomPhoto && styles.zoomPhoto)}
      uniqueId={uniqueId}
      items={photos}
      hasPaginationGradient={hasPaginationGradient}
      renderItem={(photo, index) => {
        const src = typeof photo === 'string' ? photo : photo.url;
        const alt = getAltText(photo, index);

        return <Image src={src} alt={alt} className={styles.image} loading={imgLoading} unoptimized width={0} height={0} />;
      }}
      getItemKey={(photo, index) => `PhotoCarousel_${index}_${typeof photo === 'string' ? photo : photo.url}`}
      onFirstTouch={loadPhotos}
      arrowStyle={arrowStyle}
      onStartInteracting={onStartInteracting}
      onStopInteracting={onStopInteracting}
      loop
      showPaginationDots
      hideSlideOverflow
    />
  );
};

export default PhotoCarousel;
